import { clearSession, getSession, isTokenExpired, setSession } from './session'
import { navigateToAppRoute } from './appNavigator'
import type { LoginResponse } from '../features/auth/types'
import { authLog } from '../features/auth/debug'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

type RequestOptions = {
  method?: HttpMethod
  open?: boolean
  query?: Record<string, string | number | boolean | null | undefined>
  body?: unknown
  formData?: FormData
  headers?: Record<string, string>
  skipRefresh?: boolean
}

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

const API_BASE_URL =
  (import.meta.env?.['VITE_API_BASE_URL'] as string | undefined) ||
  (isLocalhost ? 'http://localhost:5001' : 'https://api-dev.dashboardinsights.com/Prod')

let refreshPromise: Promise<boolean> | null = null
let loginRedirectInFlight = false

export class ApiRequestError extends Error {
  status: number
  code?: string
  payload?: unknown

  constructor(message: string, status: number, code?: string, payload?: unknown) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = code
    this.payload = payload
  }
}

async function extractErrorDetails(response: Response) {
  let message = 'Request failed'
  let code: string | undefined
  let payload: unknown

  const contentType = response.headers.get('content-type') || ''

  try {
    if (contentType.includes('application/json')) {
      const errorPayload = await response.json()
      payload = errorPayload
      message =
        errorPayload?.message ||
        errorPayload?.Message ||
        errorPayload?.error ||
        errorPayload?.detail ||
        errorPayload?.title ||
        message
      code = errorPayload?.code || errorPayload?.Code
      authLog('HTTP error JSON payload extracted', {
        status: response.status,
        statusText: response.statusText,
        code,
        message,
        payload,
      })
      return { message, code, payload }
    }

    const text = (await response.text()).trim()
    if (text) {
      message = text
      payload = text
      authLog('HTTP error text payload extracted', {
        status: response.status,
        statusText: response.statusText,
        message,
      })
    } else if (response.statusText) {
      message = response.statusText
      authLog('HTTP error used statusText fallback', {
        status: response.status,
        statusText: response.statusText,
      })
    }
  } catch {
    if (response.statusText) {
      message = response.statusText
    }
    authLog('HTTP error payload extraction failed', {
      status: response.status,
      statusText: response.statusText,
      contentType,
    })
  }

  return { message, code, payload }
}

function buildUrl(path: string, open: boolean, query?: RequestOptions['query']) {
  const prefix = open ? 'open/api' : 'api'
  const base = API_BASE_URL.replace(/\/$/, '')
  const cleanPath = path.replace(/^\/+/, '')
  const url = new URL(`${base}/${prefix}/${cleanPath}`)

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return
    url.searchParams.set(key, String(value))
  })

  return url.toString()
}

async function tryRefreshToken() {
  const session = getSession()
  if (!session?.refreshToken) return false

  const response = await fetch(buildUrl('ui/user/getaccesstoken', true), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
    credentials: 'include',
  })

  if (!response.ok) return false
  const payload = (await response.json()) as LoginResponse
  const { idToken, refreshToken, expiry } = payload
  if (!idToken || !refreshToken || expiry == null) return false

  const expiryDate = new Date()
  expiryDate.setSeconds(expiryDate.getSeconds() + Number(expiry) - 0)
  setSession({
    ...session,
    idToken,
    refreshToken,
    tokenExpiry: expiryDate.toISOString(),
  })
  return true
}

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = tryRefreshToken()
      .catch((error) => {
        authLog('Token refresh request failed', error)
        return false
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  const ok = await refreshPromise
  if (!ok) clearSession()
  return ok
}

export async function refreshSessionFromGuard() {
  return refreshSession()
}

async function ensureValidToken() {
  const session = getSession()
  if (!session) return false
  if (!isTokenExpired(session)) return true

  return refreshSession()
}

function redirectToLogin() {
  if (typeof window === 'undefined' || loginRedirectInFlight) return

  loginRedirectInFlight = true
  const from = `${window.location.pathname}${window.location.search}${window.location.hash}`
  navigateToAppRoute('/login', {
    replace: true,
    state: { from },
  })

  window.setTimeout(() => {
    loginRedirectInFlight = false
  }, 0)
}

async function performRequest(path: string, options: RequestOptions = {}) {
  const { method = 'GET', open = false, query, body, formData, headers, skipRefresh = false } = options

  if (!open && !skipRefresh) {
    const valid = await ensureValidToken()
    if (!valid) {
      clearSession()
      redirectToLogin()
      throw new ApiRequestError('Authentication required', 401, 'UNAUTHORIZED')
    }
  }

  const session = getSession()
  const hasFormData = typeof FormData !== 'undefined' && formData instanceof FormData
  const response = await fetch(buildUrl(path, open, query), {
    method,
    headers: {
      ...(headers || {}),
      ...(hasFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(open || !session?.idToken ? {} : { Authorization: session.idToken }),
    },
    body: hasFormData ? formData : body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  })

  if (path.includes('confirmationEmail') || path.includes('process/email')) {
    authLog('Auth-related HTTP response received', {
      path,
      method,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      contentType: response.headers.get('content-type'),
    })
  }

  if (response.status === 401 && !open && !skipRefresh) {
    const refreshed = await refreshSession()
    if (refreshed) return performRequest(path, { ...options, skipRefresh: true })
    clearSession()
    redirectToLogin()
  }

  return response
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await performRequest(path, options)

  if (!response.ok) {
    const { message, code, payload } = await extractErrorDetails(response)
    throw new ApiRequestError(message, response.status, code, payload)
  }

  if (response.status === 204 || response.status === 205) return undefined as T

  const text = await response.text()
  if (!text.trim()) return undefined as T

  return JSON.parse(text) as T
}

export async function apiRequestBlob(path: string, options: RequestOptions = {}) {
  const response = await performRequest(path, options)
  if (!response.ok) {
    const { message, code, payload } = await extractErrorDetails(response)
    throw new ApiRequestError(message, response.status, code, payload)
  }
  return {
    blob: await response.blob(),
    headers: response.headers,
  }
}
